import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Box, Paper, Typography, Button, TextField, CircularProgress, Chip, Alert, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DatasetSelector from '../Common/DatasetSelector';

const PythonPlayground = () => {
  const [code, setCode] = useState(`# Datasets: df1, df2, df3... + pd, np
# SELECT code and click "Run Selection" to run only that part
# Or click "Run All" to run everything
#
# print(df1.head())
# df = pd.merge(df1, df2, on='key')
# df1['new_col'] = df1['a'] + df1['b']
`);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [datasets, setDatasets] = useState([{ id: null, name: '', columns: [] }]);
  const [saveMode, setSaveMode] = useState('new');
  const [saveTarget, setSaveTarget] = useState(0);
  const [saveVar, setSaveVar] = useState('df');
  const [saveFilename, setSaveFilename] = useState('playground_result');
  const codeRef = useRef(null);

  const hdl = (data, index) => {
    if (!data || !data.id) return;
    const newDatasets = [...datasets];
    newDatasets[index] = { id: data.id, name: String(data.name || 'Dataset'), columns: Array.isArray(data.columns) ? data.columns : [] };
    setDatasets(newDatasets);
  };

  const addDataset = () => setDatasets([...datasets, { id: null, name: '', columns: [] }]);

  const resetSession = async () => {
    const validIds = datasets.filter(d => d.id).map(d => d.id);
    if (validIds.length === 0) return;
    await axios.post('/playground/reset', { dataset_ids: validIds });
    setOutput(''); setError('');
    toast.success('Data reset');
  };
  const removeDataset = (i) => { if (datasets.length > 1) setDatasets(datasets.filter((_, idx) => idx !== i)); };

  const getSelectedCode = () => {
    const textarea = document.querySelector('textarea');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      if (start !== end) return code.substring(start, end);
    }
    return null;
  };

  const execCode = async (codeToRun) => {
    const validIds = datasets.filter(d => d.id).map(d => d.id);
    if (validIds.length === 0) { setError('Select at least one dataset'); return null; }
    setLoading(true); setOutput(''); setError('');
    try {
      const res = await axios.post('/playground/execute', { dataset_ids: validIds, code: codeToRun });
      setOutput(res.data.output || '');
      if (res.data.error) setError(res.data.error);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Execution failed');
      return null;
    } finally { setLoading(false); }
  };

  const runAll = () => execCode(code);
  
  const runSelection = () => {
    const selected = getSelectedCode();
    if (!selected) { setError('Select some code in the editor first'); return; }
    execCode(selected);
  };

  const saveResult = async () => {
    const validIds = datasets.filter(d => d.id).map(d => d.id);
    if (validIds.length === 0) { setError('Select at least one dataset'); return; }
    if (!saveVar.trim()) { setError('Enter a variable name to save'); return; }
    setSaveLoading(true); setError('');
    try {
      const payload = { dataset_ids: validIds, code };
      let res;
      if (saveMode === 'existing') {
        res = await axios.post('/playground/execute-save', { ...payload, save_target: saveTarget, save_var: saveVar.trim() });
      } else {
        if (!saveFilename.trim()) { setError('Enter a filename'); setSaveLoading(false); return; }
        res = await axios.post('/playground/save', { ...payload, result_var: saveVar.trim(), new_filename: saveFilename.trim() });
      }
      setOutput(res.data.output || '');
      if (res.data.error) setError(res.data.error);
      if (res.data.message) alert(res.data.message);
      if (res.data.saved) setOutput(prev => prev + '\n\nSAVED: ' + res.data.saved);
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally { setSaveLoading(false); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 1, color: '#2c3e50' }}>🐍 Python Playground</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Datasets: <code>df1</code>, <code>df2</code>... + <code>pd</code>, <code>np</code>. Select code + "Run Selection" or "Run All".
      </Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">📊 Datasets</Typography>
          <IconButton onClick={addDataset} size="small" sx={{ color: '#1abc9c' }}><AddIcon /></IconButton>
        </Box>
        {datasets.map((ds, i) => (
          <Box key={i} sx={{ mb: 1.5, pb: 1, borderBottom: i < datasets.length-1 ? '1px solid #eee' : 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip label={`df${i+1}`} size="small" sx={{ bgcolor: '#1abc9c', color: 'white', fontWeight: 600, fontSize: 11, height: 22 }} />
              {ds.name ? <Typography variant="body2" fontWeight={500}>{ds.name}</Typography> : null}
              {datasets.length > 1 && <IconButton onClick={() => removeDataset(i)} size="small" sx={{ ml: 'auto', color: '#e74c3c' }}><DeleteIcon fontSize="small" /></IconButton>}
            </Box>
            <DatasetSelector apiPrefix="/playground" onDatasetLoaded={(data) => hdl(data, i)} />
            {ds.columns.length > 0 && (
              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {ds.columns.slice(0, 20).map(c => <Chip key={c} label={c} size="small" sx={{ fontSize: 9, height: 18, bgcolor: '#ecf0f1' }} />)}
                {ds.columns.length > 20 && <Chip label={`+${ds.columns.length-20}`} size="small" sx={{ fontSize: 9, height: 18 }} />}
              </Box>
            )}
          </Box>
        ))}
      </Paper>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>💻 Code Editor</Typography>
        <TextField multiline fullWidth minRows={14} maxRows={30} value={code} onChange={e => setCode(e.target.value)}
          inputRef={codeRef}
          sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: 13, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1 } }} />
        
        {/* Run buttons */}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
            onClick={runAll} disabled={loading} sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' }, fontSize: 12 }}>
            {loading ? 'Running...' : 'Run All'}
          </Button>
          <Button variant="outlined" startIcon={<PlayArrowIcon />}
            onClick={runSelection} disabled={loading} sx={{ fontSize: 12, borderColor: '#3498db', color: '#3498db' }}>
            Run Selection
          </Button>
          <Button startIcon={<DeleteIcon />} onClick={() => { setOutput(''); setError(''); }} sx={{ fontSize: 12, color: '#7f8c8d' }}>Clear</Button>
          <Button onClick={resetSession} size="small" sx={{ fontSize: 11, color: '#e67e22' }}>Reset Data</Button>
        </Box>

        {/* Save buttons - separate */}
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #eee' }}>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>💾 Save Result</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Button variant="contained" startIcon={saveLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
              onClick={saveResult} disabled={saveLoading} sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' }, fontSize: 12, height: '32px' }}>
              {saveLoading ? 'Saving...' : 'Save'}
            </Button>
            <Box>
              <label style={{ fontSize: 9, color: '#888', display: 'block' }}>Mode</label>
              <select value={saveMode} onChange={e => setSaveMode(e.target.value)} style={{ padding: '5px 8px', fontSize: 11, border: '1px solid #ddd', borderRadius: 4 }}>
                <option value="new">New file</option>
                <option value="existing">Overwrite existing</option>
              </select>
            </Box>
            <Box>
              <label style={{ fontSize: 9, color: '#888', display: 'block' }}>Variable</label>
              <input type="text" value={saveVar} onChange={e => setSaveVar(e.target.value)} placeholder="df"
                style={{ width: 70, padding: '5px 8px', fontSize: 11, border: '1px solid #ddd', borderRadius: 4 }} />
            </Box>
            {saveMode === 'existing' && (
              <Box>
                <label style={{ fontSize: 9, color: '#888', display: 'block' }}>Target Dataset</label>
                <select value={saveTarget} onChange={e => setSaveTarget(parseInt(e.target.value))} style={{ padding: '5px 8px', fontSize: 11, border: '1px solid #ddd', borderRadius: 4 }}>
                  {datasets.filter(d => d.id).map((d, i) => (
                    <option key={i} value={i}>{d.name || `df${i+1}`}</option>
                  ))}
                </select>
              </Box>
            )}
            {saveMode === 'new' && (
              <Box>
                <label style={{ fontSize: 9, color: '#888', display: 'block' }}>Filename</label>
                <input type="text" value={saveFilename} onChange={e => setSaveFilename(e.target.value)} placeholder="my_result"
                  style={{ width: 140, padding: '5px 8px', fontSize: 11, border: '1px solid #ddd', borderRadius: 4 }} />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      {(output || error) && (
        <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>📤 Output</Typography>
          {error && <Alert severity="error" sx={{ mb: 1, fontSize: 12, fontFamily: 'monospace' }}>{String(error)}</Alert>}
          {output && <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{output}</Box>}
        </Paper>
      )}
    </Box>
  );
};

export default PythonPlayground;
